export const useCart = () => {
  const LIMIT_ONE_PER_COURSE = true
  const cart = useLocalStorage('cart', [] as CartItem[])

  function normalizeQuantity(quantity: number) {
    if (LIMIT_ONE_PER_COURSE) {
      return quantity > 0 ? 1 : 0
    }
    return Math.max(0, quantity)
  }

  function addToCart(product: CoursesRecord, quantity: number) {
    const nextQuantity = normalizeQuantity(quantity)
    if (nextQuantity <= 0) {
      return
    }

    const existingItem = cart.value.find((item) => item.id === product.id)
    if (existingItem) {
      existingItem.quantity = normalizeQuantity(existingItem.quantity + nextQuantity)
    } else {
      const image = getImagePath(
        {
          collectionId: product.collectionId as string,
          id: product.id as string,
          src: product.image as string
        },
        '100x100'
      )
      cart.value.push({
        id: product.id,
        name: product.title,
        price: product.price ?? 0,
        vatRate: product.vat_rate ?? 0,
        image: image,
        quantity: nextQuantity
      })
    }
  }

  function updateCartItemQuantity(productId: string, quantity: number) {
    const item = cart.value.find((item) => item.id === productId)
    const nextQuantity = normalizeQuantity(quantity)
    if (item && nextQuantity > 0) {
      item.quantity = nextQuantity
      return
    }
    removeFromCart(productId)
  }

  function removeFromCart(productId: string) {
    cart.value = cart.value.filter((item) => item.id !== productId)
  }

  function clearCart() {
    cart.value = []
  }

  function goToCart() {
    navigateTo('/cart')
  }

  function goToCheckout() {
    navigateTo('/checkout')
  }

  // Helper for individual row totals
  function getItemTotalIncVat(item: CartItem) {
    const lineTotal = item.price * item.quantity
    const vatMultiplier = 1 + item.vatRate / 100
    return Math.round(lineTotal * vatMultiplier)
  }

  // --- Cart Summaries (Kept in Pence) ---
  const totalItems = computed(() => cart.value.reduce((sum, item) => sum + item.quantity, 0))

  const cartSubtotal = computed(() =>
    cart.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
  )

  const cartTotalVat = computed(() =>
    cart.value.reduce((sum, item) => {
      const itemVat = item.price * item.quantity * (item.vatRate / 100)
      return sum + Math.round(itemVat)
    }, 0)
  )

  const cartGrandTotal = computed(() => cartSubtotal.value + cartTotalVat.value)

  return {
    cart,
    addToCart,
    removeFromCart,
    clearCart,
    goToCart,
    goToCheckout,
    totalItems,
    cartSubtotal,
    cartTotalVat,
    cartGrandTotal,
    getItemTotalIncVat,
    updateCartItemQuantity
  }
}
